import { useEffect as useEffectOAuth, useState as useStateOAuth } from "react";
import { Api } from "./api";
import { Logo } from "./components";

// OAuth (Google + Microsoft) sign-in UI.
//
// Two surfaces live here:
//
//   <OAuthProviderButtons mode="login" | "link" />
//     Reusable button row dropped into the login/signup forms
//     (auth.jsx) and the account-settings Linked Accounts panel
//     (settings.jsx). Owns the "click → fetch start → window.location
//     = auth_url" sequence so callers don't repeat it.
//
//   <OAuthCallback />
//     Rendered by app.jsx when window.location.pathname is
//     "/oauth-callback". Reads the URL fragment the backend's
//     /api/auth/oauth/<provider>/callback handler dropped there, and
//     dispatches:
//        #temp=<jwt>          → POST /oauth/finalize (login flow)
//        #linked=<provider>   → set a flash, route to settings
//        #error=<reason>      → render an error with retry CTA
//
// PR B2 only covers the login flow; PR C will reuse the same finalize
// path for OAuth signup once the invite-flow (#170) lands.

// SessionStorage key for the state JWT minted by the backend on
// /oauth/<provider>/start. Stashed by OAuthProviderButton before the
// redirect, read by OAuthCallback on the way back. Read-once: cleared
// after a successful finalize OR after surfacing a callback error.
const _OAUTH_STATE_STORAGE_KEY = "cc.oauth.state";

// Decode the `nonce` claim out of a state JWT without verifying the
// signature. Safe on the SPA: the server already verified the state
// on the callback path. We just need to pluck the nonce so we can
// pass it back to /oauth/finalize as proof THIS browser started the
// OAuth dance — the server's own pending token carries the same
// nonce in a signed claim, so a mismatch refuses the request.
function _stateNonceFromStorage() {
  let raw;
  try { raw = window.sessionStorage.getItem(_OAUTH_STATE_STORAGE_KEY); }
  catch (_) { return null; }
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length !== 3) return null;
  try {
    // Base64url decode the payload — JWT payloads are URL-safe base64.
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return payload && typeof payload.nonce === "string" ? payload.nonce : null;
  } catch (_) {
    return null;
  }
}

function _clearStoredOAuthState() {
  try { window.sessionStorage.removeItem(_OAUTH_STATE_STORAGE_KEY); }
  catch (_) { /* ignore */ }
}

// Display labels (and accessible names) for each provider. The
// authoritative list is PROVIDERS in app/auth/oauth.py — keep these
// in sync.
const OAUTH_PROVIDERS = [
  { key: "google", label: "Google" },
  { key: "microsoft", label: "Microsoft" },
  { key: "apple", label: "Apple" },
];

// True when the SPA is running inside the Capacitor iOS WebView.
// Detected via window.Capacitor + platform=="ios" — works regardless
// of document scheme. The original implementation checked
// location.protocol === "capacitor:", which works for bundled-asset
// mode but is ALWAYS FALSE when we use server.url because the
// document is loaded from https://communitycompass.ai (so the scheme
// is https:, not capacitor:). That false negative made the SPA fall
// back to window.location.assign on Google sign-in clicks, which
// landed Google's Use-Secure-Browsers block in the in-app WebView.
function _isCapacitorIOS() {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;
    const platform =
      (typeof cap.getPlatform === "function" && cap.getPlatform()) ||
      cap.platform;
    return platform === "ios";
  } catch (_) {
    return false;
  }
}

// Custom URL scheme registered in ios/App/App/Info.plist. The backend
// redirects OAuth callbacks here when state.platform == "ios", and
// ASWebAuthenticationSession waits for this scheme to close itself
// and hand the callback URL back to the app.
const _IOS_CALLBACK_SCHEME = "aicommunitycompassios";

// True when the iOS Capacitor app has the CCOAuthBrowser native plugin
// loaded (which wraps ASWebAuthenticationSession). The plugin is
// registered in AppDelegate.swift; if it's missing (older build,
// future Android target without an equivalent) we fall back to the
// hide-the-button behavior so users don't tap into a broken flow.
function _hasIOSOAuthBrowser() {
  try {
    return !!(window.Capacitor
      && window.Capacitor.Plugins
      && window.Capacitor.Plugins.CCOAuthBrowser);
  } catch (_) {
    return false;
  }
}

// Providers that need the proper iOS OAuth flow (ASWebAuthenticationSession)
// because they reject embedded WebViews. Google enforces this via
// OAuth 2.0 Policy "Use Secure Browsers"; Microsoft historically does
// not but the iOS-flavored flow works fine for it too. The same set
// applies on Android (Google blocks embedded WebViews there too).
const _PROVIDERS_NEEDING_IOS_OAUTH_BROWSER = new Set(["google", "apple"]);

// True when running inside the Capacitor Android WebView.
function _isCapacitorAndroid() {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;
    const platform =
      (typeof cap.getPlatform === "function" && cap.getPlatform()) ||
      cap.platform;
    return platform === "android";
  } catch (_) {
    return false;
  }
}

// The custom scheme the Android app registers (AndroidManifest intent-filter).
// The backend deep-links here on ?platform=android; keep in sync with
// _ANDROID_CALLBACK_SCHEME_URL in app/auth/routes.py.
const _ANDROID_CALLBACK_SCHEME = "aicommunitycompassandroid://";

// True when all named Capacitor plugins are present at runtime. Android
// OAuth needs Browser (Chrome Custom Tabs) + App (appUrlOpen deep link).
function _hasCapacitorPlugins(...names) {
  try {
    const p = window.Capacitor && window.Capacitor.Plugins;
    return !!p && names.every((n) => !!p[n]);
  } catch (_) {
    return false;
  }
}

// Pre-baked SVG marks. Inline so the SPA's strict CSP doesn't have
// to add fonts.gstatic.com or unpkg paths for one icon each. The
// `viewBox` and path data come from the providers' published brand
// kits (Google "G", Microsoft 4-tile logo).
function OAuthProviderIcon({ provider }) {
  if (provider === "google") {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.63z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.83.86-3.06.86-2.35 0-4.35-1.59-5.06-3.72H.96v2.33A8.99 8.99 0 0 0 9 18z"/>
        <path fill="#FBBC05" d="M3.94 10.7A5.41 5.41 0 0 1 3.66 9c0-.59.1-1.17.28-1.7V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.98-2.34z"/>
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58A8.99 8.99 0 0 0 9 0 8.99 8.99 0 0 0 .96 4.96L3.94 7.3C4.65 5.17 6.65 3.58 9 3.58z"/>
      </svg>
    );
  }
  if (provider === "microsoft") {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <rect x="0" y="0" width="8" height="8" fill="#F25022"/>
        <rect x="10" y="0" width="8" height="8" fill="#7FBA00"/>
        <rect x="0" y="10" width="8" height="8" fill="#00A4EF"/>
        <rect x="10" y="10" width="8" height="8" fill="#FFB900"/>
      </svg>
    );
  }
  if (provider === "apple") {
    // Apple logo mark. `currentColor` so it inherits the button's text
    // color (dark on the cream theme), consistent with the other marks.
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M16.365 1.43c0 1.14-.42 2.2-1.13 3.02-.84.97-2.2 1.72-3.34 1.63-.13-1.13.43-2.31 1.1-3.05.79-.86 2.2-1.5 3.37-1.6zM20.5 17.2c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1.01-4.03-1-2.09.01-2.53 1.02-4.07 1-1.73-.01-3.06-1.76-4.05-3.32C-.06 17.2-.4 12.36 1.36 9.79c1.02-1.49 2.62-2.36 4.13-2.36 1.54 0 2.5 1.02 3.78 1.02 1.24 0 1.99-1.02 3.77-1.02 1.34 0 2.76.73 3.77 1.99-3.31 1.81-2.77 6.54.69 7.78z"/>
      </svg>
    );
  }
  return null;
}

// Single button — wraps the start-the-OAuth-flow logic so callers
// only render <OAuthProviderButtons /> and pass mode.
function OAuthProviderButton({ provider, label, mode, busy, onBusy, onError }) {
  const click = async () => {
    if (busy) return;
    onBusy(true);
    onError(null);
    try {
      // On Capacitor iOS we tell the backend to use the custom URL
      // scheme as the eventual callback redirect, AND we open the
      // auth URL in ASWebAuthenticationSession (via our native
      // plugin) instead of a regular page navigation. Web flows are
      // unchanged.
      const useIOSOAuthBrowser = mode === "login"
        && _isCapacitorIOS()
        && _hasIOSOAuthBrowser();
      // Android: same problem (embedded WebView OAuth is blocked), solved
      // with Chrome Custom Tabs (@capacitor/browser) + a custom-scheme
      // deep link captured via @capacitor/app's appUrlOpen.
      const useAndroidOAuthBrowser = mode === "login"
        && _isCapacitorAndroid()
        && _hasCapacitorPlugins("Browser", "App");

      const platform = useIOSOAuthBrowser ? "ios"
        : useAndroidOAuthBrowser ? "android"
        : undefined;
      const data = mode === "link"
        ? await Api.oauthLinkStart(provider)
        : await Api.oauthStart(provider, platform);
      if (!data || !data.auth_url || !data.state) {
        throw new Error("OAuth provider isn't set up. Try again later.");
      }
      // Stash the state JWT in sessionStorage before redirecting to
      // the IdP. OAuthCallback reads it back and pulls the `nonce`
      // claim to pass as state_nonce on /oauth/finalize — the server
      // cross-checks against the same nonce baked into the pending
      // token. Without this, a captured #temp= URL fragment from an
      // attacker's own OAuth flow could be replayed against a victim's
      // browser via a crafted /oauth-callback link and log the victim
      // in as the attacker. Codex P1 on PR #185.
      try {
        window.sessionStorage.setItem(_OAUTH_STATE_STORAGE_KEY, data.state);
      } catch (_) { /* storage disabled — finalize will refuse, which is right */ }

      if (useIOSOAuthBrowser) {
        // ASWebAuthenticationSession blocks until either (a) it sees
        // a URL with our custom scheme (success) or (b) the user
        // cancels. Resolve carries `cancelled` + `callbackUrl`.
        const result = await window.Capacitor.Plugins.CCOAuthBrowser.open({
          url: data.auth_url,
          callbackScheme: _IOS_CALLBACK_SCHEME,
        });
        if (result.cancelled) {
          // User aborted — quietly reset the button. No error toast.
          onBusy(false);
          return;
        }
        // Navigate the SPA's WebView to /oauth-callback carrying the
        // same fragment the web flow would have landed with. The
        // OAuthCallback component does the rest (reads state_nonce
        // from sessionStorage, calls /oauth/finalize, dispatches login).
        // Parse via string-slice rather than `new URL` because
        // WebKit's URL constructor has historically been spotty about
        // custom schemes and we only care about the hash anyway.
        const callbackUrl = result.callbackUrl || "";
        const hashIdx = callbackUrl.indexOf("#");
        const hash = hashIdx >= 0 ? callbackUrl.substring(hashIdx) : "";
        window.location.assign("/oauth-callback" + hash);
        return;
      }

      if (useAndroidOAuthBrowser) {
        const Browser = window.Capacitor.Plugins.Browser;
        const App = window.Capacitor.Plugins.App;
        // Chrome Custom Tabs can't return a value, so the backend
        // deep-links to aicommunitycompassandroid://oauth-callback#...
        // when done. Listen for that via App.appUrlOpen, close the tab,
        // and forward the fragment to /oauth-callback (same as iOS).
        let settled = false;
        const urlListener = await App.addListener("appUrlOpen", (event) => {
          const url = (event && event.url) || "";
          if (settled || url.indexOf(_ANDROID_CALLBACK_SCHEME) !== 0) return;
          settled = true;
          urlListener.remove();
          try { Browser.close(); } catch (_) {}
          const hashIdx = url.indexOf("#");
          window.location.assign(
            "/oauth-callback" + (hashIdx >= 0 ? url.substring(hashIdx) : ""),
          );
        });
        // If the user dismisses the tab without finishing, reset the
        // button (no callback will ever arrive).
        const finishListener = await Browser.addListener("browserFinished", () => {
          finishListener.remove();
          if (settled) return;
          urlListener.remove();
          onBusy(false);
        });
        await Browser.open({ url: data.auth_url });
        return;
      }

      // Web (and link-mode on any platform): full-page navigation.
      // Leaves this page — no need to flip busy back; we're navigating.
      window.location.assign(data.auth_url);
    } catch (err) {
      onBusy(false);
      // 503 provider_not_configured surfaces here when the operator
      // hasn't set the client id/secret env vars. Show a quieter
      // message than "Request failed (503)".
      const msg = (err && err.code === "provider_not_configured")
        ? `${label} sign-in isn't configured for this site yet.`
        : (err && err.message) || `Couldn't start ${label} sign-in.`;
      onError(msg);
    }
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className="btn btn--ghost btn--block"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 10, fontWeight: 500,
      }}
    >
      <OAuthProviderIcon provider={provider} />
      <span>
        {mode === "link"
          ? `Link ${label}`
          : `Continue with ${label}`}
      </span>
    </button>
  );
}

// Button row for use in any auth surface. `mode` selects between
// login (anonymous) and link (authenticated). The "or use email
// below" divider is rendered by the caller — we don't assume the
// caller has an email form following us.
function OAuthProviderButtons({ mode = "login" }) {
  const [busy, setBusy] = useStateOAuth(false);
  const [err, setErr] = useStateOAuth(null);

  // On Capacitor iOS we only hide a provider if it BOTH requires the
  // proper ASWebAuthenticationSession path AND the CCOAuthBrowser
  // native plugin isn't loaded. Once the plugin is in the build,
  // Google's button is back. Older builds (pre-plugin) still safely
  // hide it.
  const onCapIOSWithoutPlugin = _isCapacitorIOS() && !_hasIOSOAuthBrowser();
  const onCapAndroidWithoutPlugin =
    _isCapacitorAndroid() && !_hasCapacitorPlugins("Browser", "App");
  const visible = (onCapIOSWithoutPlugin || onCapAndroidWithoutPlugin)
    ? OAUTH_PROVIDERS.filter(
        (p) => !_PROVIDERS_NEEDING_IOS_OAUTH_BROWSER.has(p.key),
      )
    : OAUTH_PROVIDERS;

  return (
    <div className="stack-sm" style={{ marginBottom: 12 }}>
      {visible.map(({ key, label }) => (
        <OAuthProviderButton
          key={key}
          provider={key}
          label={label}
          mode={mode}
          busy={busy}
          onBusy={setBusy}
          onError={setErr}
        />
      ))}
      {err && (
        <div className="muted" style={{ fontSize: 13, color: "var(--danger, #b03030)" }}>
          {err}
        </div>
      )}
    </div>
  );
}

// Parse the URL fragment that the backend callback drops on the SPA.
// Returns an object describing the next action, or null if the
// fragment is empty / unrecognized (the caller can fall through to
// the normal auth screen).
function _parseOAuthFragment(hash) {
  const raw = (hash || "").replace(/^#/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  if (params.has("temp")) return { kind: "login", temp_token: params.get("temp") };
  if (params.has("linked")) return { kind: "linked", provider: params.get("linked") };
  if (params.has("error")) return { kind: "error", reason: params.get("error") };
  return null;
}

// Friendly UX strings for each backend error code. Anything not in
// this map falls through to the raw code; that's a feature, not a
// bug — surfacing the code makes it diagnosable for an operator who
// looks over the user's shoulder.
const OAUTH_ERROR_COPY = {
  "no_account": "No Community Compass account uses that email yet. Sign up first, then come back to link this provider.",
  "account_inactive": "That account is inactive. Contact your community manager.",
  "user_cancelled": "Sign-in was cancelled.",
  "state_invalid": "That sign-in link expired. Try again.",
  "provider_not_configured": "Sign-in via this provider isn't set up for this site yet.",
  "unknown_provider": "We don't recognize that sign-in provider.",
  "missing_params": "Sign-in link was malformed. Try again.",
  "link_failed:already_linked_to_other_user": "That provider account is already linked to a different Community Compass user.",
  "link_failed:user_unavailable": "Your account changed during the sign-in flow. Sign in again and retry.",
  "link_failed:state_missing_user": "That link request expired. Try again from Account Settings.",
  // Operator-side: the droplet's venv is missing a package (most
  // commonly `cryptography`, PyJWT's RS256 backend). Tell the user
  // to contact support — they can't self-recover from this and
  // shouldn't see a confusing "id_token_invalid".
  "oauth_failed:backend_misconfigured": "Sign-in is temporarily unavailable on our side. We've been notified — please try again in a few minutes or use email + password for now.",
};

function _errorCopy(reason) {
  if (!reason) return "Sign-in didn't complete. Try again.";
  if (OAUTH_ERROR_COPY[reason]) return OAUTH_ERROR_COPY[reason];
  // oauth_failed:<code> bucket — strip the prefix for display so the
  // user sees the precise reason without the namespace noise.
  if (reason.startsWith("oauth_failed:")) {
    return `Sign-in failed (${reason.slice("oauth_failed:".length)}). Try again.`;
  }
  if (reason.startsWith("link_failed:")) {
    return `Linking failed (${reason.slice("link_failed:".length)}). Try again from Account Settings.`;
  }
  return `Sign-in didn't complete (${reason}). Try again.`;
}

// The page rendered at /oauth-callback. Owns the temp_token →
// finalize → session handoff and the link-success / error paths.
//
// Props:
//   onLogin(role, user)  — same callback AuthScreen receives; called
//                          on successful finalize.
function OAuthCallback({ onLogin }) {
  const [parsed] = useStateOAuth(() => _parseOAuthFragment(window.location.hash));
  const [phase, setPhase] = useStateOAuth("working");  // working | picker | error | linked
  const [errorMsg, setErrorMsg] = useStateOAuth(null);
  const [multi, setMulti] = useStateOAuth(null);  // [{id,slug,name,state}]
  const [tempToken, setTempToken] = useStateOAuth(null);

  // Wipe the fragment as soon as we've captured it — keeps the temp
  // token out of any analytics / history snapshot the user might
  // copy out of their address bar.
  useEffectOAuth(() => {
    try {
      const u = new URL(window.location.href);
      u.hash = "";
      window.history.replaceState({}, "", u.toString());
    } catch (_) { /* ignore */ }
  }, []);

  // Drive the flow based on the parsed fragment.
  useEffectOAuth(() => {
    if (!parsed) {
      setPhase("error");
      setErrorMsg("This page only exists to finish a sign-in. Head back to the home page.");
      return;
    }
    if (parsed.kind === "error") {
      setPhase("error");
      setErrorMsg(_errorCopy(parsed.reason));
      // The OAuth dance is over (failed); the state nonce is no longer
      // useful and shouldn't sit in sessionStorage waiting for a
      // retry attempt to reuse it.
      _clearStoredOAuthState();
      return;
    }
    if (parsed.kind === "linked") {
      setPhase("linked");
      _clearStoredOAuthState();
      // Briefly show "linked", then send the user back to the
      // account-settings page to see the updated list. 1.2s is long
      // enough to register the toast, short enough not to feel slow.
      const t = setTimeout(() => {
        // The path is the SPA root; the route param tells app.jsx to
        // open Settings with the linked-accounts tab active.
        try {
          const u = new URL(window.location.href);
          u.pathname = "/";
          u.searchParams.set("route", "settings");
          u.searchParams.set("settings_tab", "security");
          window.location.assign(u.toString());
        } catch (_) {
          window.location.assign("/");
        }
      }, 1200);
      return () => clearTimeout(t);
    }
    if (parsed.kind === "login") {
      setTempToken(parsed.temp_token);
      _finishLogin(parsed.temp_token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed]);

  // Call /oauth/finalize. If the user belongs to multiple
  // communities, the response carries `multi_community: true` and a
  // `communities` list — flip into picker phase and re-call once the
  // user picks.
  async function _finishLogin(temp_token, picked) {
    // Pull the state nonce stored by OAuthProviderButton on /start.
    // Server cross-checks against the nonce baked into the pending
    // token. If missing or mismatched, server returns 401 — which is
    // exactly right when a captured #temp= URL is opened by a browser
    // that never initiated this OAuth flow. Codex P1 on PR #185.
    const state_nonce = _stateNonceFromStorage();
    if (!state_nonce) {
      setPhase("error");
      setErrorMsg("This browser didn't start the sign-in. Open Community Compass and click Continue with Google from there.");
      _clearStoredOAuthState();
      return;
    }
    try {
      const data = await Api.oauthFinalize({
        temp_token,
        state_nonce,
        community_id: picked ? picked.id : undefined,
      });
      if (data && data.multi_community) {
        setMulti(data.communities || []);
        setPhase("picker");
        // Keep state_nonce in storage so the picker's follow-up
        // finalize call (in this same browser, this same tab) still
        // has the nonce available. Cleared on successful login below.
        return;
      }
      if (data && data.token && data.user) {
        // Same handoff AuthScreen does on a successful password login.
        const role = Api.uiRole(data.user.role);
        _clearStoredOAuthState();
        onLogin && onLogin(role, data.user);
        // Replace the URL with the SPA root so a refresh doesn't
        // bounce through /oauth-callback with no fragment.
        try {
          const u = new URL(window.location.href);
          u.pathname = "/";
          window.history.replaceState({}, "", u.toString());
        } catch (_) { /* ignore */ }
        return;
      }
      // Backend handed back something we don't recognize — surface
      // generically rather than silently strand the user.
      setPhase("error");
      setErrorMsg("Sign-in completed but the server response was unexpected. Try again.");
      _clearStoredOAuthState();
    } catch (err) {
      setPhase("error");
      const code = err && err.code;
      setErrorMsg(_errorCopy(code) === _errorCopy(undefined) && err && err.message
        ? err.message
        : _errorCopy(code));
      _clearStoredOAuthState();
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="auth__shell">
      <div className="auth__card card card--pad-lg" style={{ maxWidth: 460, margin: "8vh auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          {typeof Logo === "function" ? <Logo size={36} /> : null}
          <h2 className="display" style={{ margin: 0, fontWeight: 500 }}>
            Community Compass
          </h2>
        </div>

        {phase === "working" && (
          <div className="stack-sm">
            <p>Finishing your sign-in…</p>
            <div className="muted" style={{ fontSize: 13 }}>
              This usually takes a second.
            </div>
          </div>
        )}

        {phase === "picker" && multi && (
          <div className="stack-sm">
            <p>You belong to more than one community. Pick which one to sign in to:</p>
            <div className="stack-xs">
              {multi.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="btn btn--ghost btn--block"
                  onClick={() => {
                    setPhase("working");
                    _finishLogin(tempToken, c);
                  }}
                  style={{ textAlign: "left" }}
                >
                  <strong>{c.name}</strong>
                  <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
                    {c.state}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "linked" && (
          <div className="stack-sm">
            <p>Linked. Sending you back to Account Settings…</p>
          </div>
        )}

        {phase === "error" && (
          <div className="stack-sm">
            <p style={{ color: "var(--danger, #b03030)" }}>{errorMsg}</p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => { window.location.assign("/"); }}
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export { OAuthCallback, OAuthProviderButtons };
